import { useMemo, useState } from "react";
import * as FlagIcons from "country-flag-icons/react/3x2";
import { Check, ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  buildDialNumber,
  countries,
  detectCountryFromInput,
  getCountryByDialCode,
  getDefaultCountry,
  type Country,
} from "@/lib/countries";
import { cn } from "@/lib/utils";

function parsePhoneValue(value: string, fallbackCountry: Country) {
  const cleaned = value.replace(/[^0-9+]/g, "");
  if (!cleaned) return { country: fallbackCountry, localNumber: "", canonical: "" };

  if (cleaned.startsWith("+")) {
    const detected = detectCountryFromInput(cleaned);
    if (detected) {
      return {
        country: detected.country,
        localNumber: detected.localNumber,
        canonical: buildDialNumber(detected.country, detected.localNumber),
      };
    }
    return { country: fallbackCountry, localNumber: cleaned.replace(/\+/g, ""), canonical: cleaned };
  }

  const localNumber = cleaned.replace(/\+/g, "");
  return {
    country: fallbackCountry,
    localNumber: localNumber.startsWith(fallbackCountry.dialCode.replace("+", ""))
      && localNumber.length > fallbackCountry.dialCode.length + 3
      ? localNumber.slice(fallbackCountry.dialCode.replace("+", "").length)
      : localNumber,
    canonical: buildDialNumber(fallbackCountry, localNumber),
  };
}

export function CountryPhoneInput({
  value,
  onChange,
  defaultCountryCode,
  placeholder = "Phone number",
  disabled = false,
  inputTestId = "input-country-phone",
  countryTestId = "button-country-phone-country",
}: {
  value: string;
  onChange: (value: string) => void;
  defaultCountryCode?: string;
  placeholder?: string;
  disabled?: boolean;
  inputTestId?: string;
  countryTestId?: string;
}) {
  const fallbackCountry = useMemo(
    () => getCountryByDialCode(defaultCountryCode || "+1") || getDefaultCountry(),
    [defaultCountryCode],
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const selectedCountry = countries.find((country) => country.code === selectedCountryCode) || fallbackCountry;
  const parsedValue = parsePhoneValue(value, selectedCountry);
  const displayedCountry = value ? parsedValue.country : selectedCountry;
  const localNumber = parsedValue.localNumber;

  const filteredCountries = useMemo(() => {
    const search = countrySearch.trim().toLowerCase();
    if (!search) return countries;
    return countries.filter((country) =>
      country.name.toLowerCase().includes(search)
      || country.code.toLowerCase().includes(search)
      || country.dialCode.includes(search)
    );
  }, [countrySearch]);

  const handleInputChange = (input: string) => {
    const cleaned = input.replace(/[^0-9+]/g, "");
    if (cleaned.startsWith("+")) {
      const detected = detectCountryFromInput(cleaned);
      if (detected) {
        setSelectedCountryCode(detected.country.code);
        const canonical = buildDialNumber(detected.country, detected.localNumber);
        onChange(canonical);
        return;
      }
    }

    const local = cleaned.replace(/\+/g, "");
    const canonical = local ? buildDialNumber(displayedCountry, local) : "";
    onChange(canonical);
  };

  const selectCountry = (country: Country) => {
    setSelectedCountryCode(country.code);
    setCountryOpen(false);
    setCountrySearch("");
    const canonical = localNumber ? buildDialNumber(country, localNumber) : "";
    onChange(canonical);
  };

  return (
    <div className="flex gap-2">
      <Popover open={countryOpen} onOpenChange={(open) => { setCountryOpen(open); if (!open) setCountrySearch(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex items-center gap-1 shrink-0 px-2 h-10 rounded-md border border-input bg-background hover-elevate cursor-pointer disabled:pointer-events-none disabled:opacity-50"
            aria-label={`Country code ${displayedCountry.dialCode}`}
            data-testid={countryTestId}
          >
            {(() => {
              const Flag = (FlagIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>)[displayedCountry.code];
              return Flag ? (
                <Flag aria-label={displayedCountry.name} className="w-5 h-3.5 rounded-[2px] shrink-0" />
              ) : null;
            })()}
            <span className="text-sm font-mono text-muted-foreground">{displayedCountry.dialCode}</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContentNoPortal className="w-64 p-0" align="start" side="bottom">
          <div className="flex items-center gap-2 px-3 py-2 border-b">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={countrySearch}
              onChange={(event) => setCountrySearch(event.target.value)}
              placeholder="Search country..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
              autoFocus
              data-testid={`${countryTestId}-search`}
            />
          </div>
          <ScrollArea className="h-56">
            <div className="p-1">
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover-elevate cursor-pointer",
                    displayedCountry.code === country.code && "bg-accent",
                  )}
                  onClick={() => selectCountry(country)}
                  data-testid={`${countryTestId}-${country.code}`}
                >
                  {(() => {
                    const Flag = (FlagIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>)[country.code];
                    return Flag ? (
                      <Flag aria-label={country.name} className="w-5 h-3.5 rounded-[2px] shrink-0" />
                    ) : null;
                  })()}
                  <span className="flex-1 min-w-0 truncate text-left" title={country.name}>{country.code}</span>
                  <span className="font-mono text-xs text-muted-foreground shrink-0 whitespace-nowrap">{country.dialCode}</span>
                  {displayedCountry.code === country.code && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">No country found</p>
              )}
            </div>
          </ScrollArea>
        </PopoverContentNoPortal>
      </Popover>
      <Input
        value={localNumber}
        onChange={(event) => handleInputChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={inputTestId}
      />
    </div>
  );
}